import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { formatDateWithoutHour, isPassed } from 'app/utils/dates';
import { isSunday } from 'app/utils/dayjs';

import { inventoryStore } from '../../InventoryStore';
import { InventoryCell } from '../../_components/Cells/InventoryCell';
import { WatchingCell } from '../../_components/Cells/WatchingCell';
import type { CellIconProps } from '../../_components/Cells/_components/CellIcon';
import { OpeningStatus, OpeningsErrorMessage } from '../../type';
import type { FormPlanning } from '../../type';
import { getCellErrors, getErrorMessage } from '../../utils';

import './Row.scss';

type InventoryRowProps = {
  dates?: Date[];
  firstColumnContent: string | ReactNode;
  roomId: number;
  packageId?: number;
  fieldName: string;
  getDescriptionValue?: (
    datePlanning: FormPlanning
  ) => number | string | ReactNode;
  isEditable: boolean;
  getValue: (datePlanning: FormPlanning) => number | boolean | string;
  disabled?: boolean;
  getIconParameters?: (datePlanning: FormPlanning) => CellIconProps;
  getError?: (datePlanning: FormPlanning) => string | undefined;
};

export const InventoryRow = ({
  dates = [],
  firstColumnContent,
  roomId,
  packageId,
  isEditable,
  fieldName,
  getDescriptionValue,
  getValue,
  disabled = false,
  getIconParameters,
  getError,
}: InventoryRowProps) => {
  const bookableDays = useStore(inventoryStore, (state) => state.bookableDays);
  const isEditMode = useStore(inventoryStore, (store) => store.isEditMode);
  const { hotelInfo } = useContext(HotelContext);

  if ((fieldName === 'published' || fieldName === 'closed') && !isEditMode) {
    return null;
  }

  return (
    <div className="table-line">
      <div className="first-column">{firstColumnContent}</div>
      {dates.map((date) => {
        const formattedDate = formatDateWithoutHour(date);
        const basePath = [
          'rooms',
          roomId.toString(),
          'plannings',
          formattedDate,
        ];
        const optAddedPath =
          packageId !== undefined
            ? ['packagePrices', packageId.toString()]
            : [];
        const nameField = [...basePath, ...optAddedPath, fieldName];

        return (
          <WatchingCell
            name={basePath}
            key={`package_inventory_${nameField.join('_')}`}
          >
            {(values?: FormPlanning) => {
              const closed =
                isPassed(date) || !bookableDays.includes(formattedDate);

              if (!values) {
                return null;
              }

              const packageValues = packageId
                ? values.packagePrices[packageId]
                : undefined;

              const packageErrors = packageValues?.errors || [];
              const errors = [...values?.errors, ...packageErrors];

              const { errors: finalErrors, classNames: cellClasses } =
                !closed && fieldName
                  ? getCellErrors(
                      errors,
                      fieldName,
                      packageValues?.published || false,
                      packageValues?.isForced || false
                    )
                  : { errors: [], classNames: [] };

              const errorMessages = !bookableDays.includes(formattedDate)
                ? [OpeningsErrorMessage[OpeningStatus.CLOSED_ON_STAYCATION]]
                : finalErrors.map((e) =>
                    getErrorMessage(e, hotelInfo?.stockScrapperEnabled || false)
                  );

              return (
                <div
                  className={classNames('other-column', ...cellClasses, {
                    'bold-border': isSunday(date),
                    closed: closed,
                    'edit-mode': isEditMode,
                  })}
                >
                  <InventoryCell
                    date={date}
                    roomId={roomId}
                    nameField={nameField}
                    name={fieldName}
                    isEditable={isEditable}
                    packageId={packageId}
                    key={nameField.join('_')}
                    datePlanning={values}
                    getValue={getValue}
                    getDescriptionValue={getDescriptionValue}
                    isEditMode={isEditMode}
                    disabled={disabled}
                    getIconParameters={getIconParameters}
                    tooltipMessage={
                      errorMessages.length > 0 ? (
                        <>
                          {errorMessages.map((e, i) => (
                            <div key={i}>{e}</div>
                          ))}
                        </>
                      ) : undefined
                    }
                    getError={getError}
                  />
                </div>
              );
            }}
          </WatchingCell>
        );
      })}
    </div>
  );
};
